import { parseJwtPayload } from "@/core_bridge/module_api";
import { getUsers } from "@/services/module_api";
import { ValueSignal } from "./state_signal";
import type { SessionState } from "./SessionState";

export class UserDirectory {
  private directory = new Map<string, string>();
  readonly directoryUpdates = new ValueSignal<number>(0);
  private sessionState: SessionState | null;

  constructor(sessionState: SessionState | null = null) {
    this.sessionState = sessionState;
  }

  async initialize(): Promise<void> {
    const users = await getUsers();
    if (users) {
      for (const user of users) {
        if (!user.userId) continue;
        const handle =
          (user.fullHandle ?? (user as any).full_handle ?? user.username ?? user.userId) as string;
        this.directory.set(user.userId, handle || user.userId);
      }
    }

    const token = this.sessionState?.getToken() || null;
    if (token) {
      const parsed = parseJwtPayload(token) || null;
      const user_id = (parsed?.user_id || parsed?.userId) as string | undefined;
      const username = (parsed?.username || parsed?.user_name) as string | null;
      const full_handle = (parsed?.full_handle || parsed?.fullHandle) as string | null;
      if (user_id) {
        const handle = full_handle || username;
        this.directory.set(user_id, handle || user_id);
      }
    }

    this.directoryUpdates.set(Date.now());
  }

  getUsername(userId: string): string {
    return this.directory.get(userId) || userId;
  }

  updateUser(userId: string, username: string): void {
    this.directory.set(userId, username || userId);
    this.directoryUpdates.set(Date.now());
  }

  getUsernameOrNull(userId: string): string | null {
    return this.directory.get(userId) || null;
  }

  updateUsers(users: Record<string, string>): void {
    for (const [userId, username] of Object.entries(users)) {
      this.directory.set(userId, username || userId);
    }
    this.directoryUpdates.set(Date.now());
  }

  removeUser(userId: string): void {
    this.directory.delete(userId);
    this.directoryUpdates.set(Date.now());
  }

  size(): number {
    return this.directory.size;
  }

  hasUser(userId: string): boolean {
    return this.directory.has(userId);
  }

  getAllUsers(): Record<string, string> {
    return Object.fromEntries(this.directory);
  }

  clear(): void {
    this.directory.clear();
    this.directoryUpdates.set(Date.now());
  }
}
